











































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { vxm } from '@/stores';
import moment from 'moment';
import { JobsViewerModeEnum } from "@/types/jobViewerMode";
import { DATE_FORMAT_READABLE, MONTH_FORMAT_READABLE } from "comunet-jobs-and-technician-repository-shared/constants";
import { calculateDates } from "comunet-jobs-and-technician-repository-shared/util";
import { Job } from '@/models/jobSummary/job';
import { JobAssignmentDatesViewModel } from '@/models/jobSummary/jobAssignmentDatesViewModel';
import JobSummary from "@/components/jobSummary/JobSummary.vue";

@Component({
  components: {
    JobSummary
  }
})
export default class JobViewer extends Vue {
  private displayDaySelector = false;
  private displayMonthSelector = false;

  @Watch("selectedDay")
  private async onSelectedDateChanged(newValue: string, oldValue: string) {
    if (moment(oldValue).year() != moment(newValue).year()) {
      // we fetch data for a year at a time to prevent overusing the network - if the year has changed then we need to get different data
      await vxm.jobs.loadJobs();
    }
  }

  async created() {
    await vxm.jobs.loadJobs();
  }

  nextDate(): void {
    vxm.jobs.goToNextDate();
  }

  previousDate(): void {
    vxm.jobs.goToPreviousDate();
  }

  get jobs(): Job[] {
    return vxm.jobs.jobs;
  }

  get startPeriod(){
    let startPeriod = moment(vxm.jobs.jobsViewerSelectedDay);

    if (vxm.jobs.jobsViewerMode == JobsViewerModeEnum.MONTH) {
      startPeriod = moment(vxm.jobs.jobsViewerSelectedMonth + "-01");
    }

    return startPeriod;
  }

  get endPeriod(){
    let endPeriod = moment(vxm.jobs.jobsViewerSelectedDay);

    if (vxm.jobs.jobsViewerMode == JobsViewerModeEnum.WEEK) {
      endPeriod = moment(endPeriod).add(6, "days"); // Today counts as 1 + 6 = 7 (i.e., a week)
    }
    else if (vxm.jobs.jobsViewerMode == JobsViewerModeEnum.MONTH) {
      endPeriod = moment(endPeriod).add(1, "months").add(-1, "days");
    }

    return endPeriod;
  }

  getJobAssignmentDates(job: Job): JobAssignmentDatesViewModel[]{
    let jobAssignmentDates = [];

    // Build a list of assignment start/ends from the job dates
    let currentAssignmentDates = null;
    let jobDates = calculateDates(
      moment(job.calculatedDates.start).format('YYYY-MM-DD'), 
      moment.utc(job.calculatedDates.end).local().format('YYYY-MM-DD')
    );

    for(let i = 0; i < jobDates.length; i++){
      let date = jobDates[i];

      let isAssignedOnDate = job.assignment.calculatedDates.length == 0 
      || job.assignment.calculatedDates.some(y => 
        (y.start == null || moment(y.start) <= moment(date)) &&
        (y.end == null || moment.utc(y.end).local() >= moment(date))
      );

      if(isAssignedOnDate){
        if(currentAssignmentDates == null){
          currentAssignmentDates = {
            start: date,
            end: date
          };
        } else {
          currentAssignmentDates.end = date;
        }
      } else {
        if(currentAssignmentDates != null){
          jobAssignmentDates.push(currentAssignmentDates);
        }
        currentAssignmentDates = null;
      }
    }

    if(currentAssignmentDates != null){
      jobAssignmentDates.push(currentAssignmentDates);
    }

    // Filter the assignment start/ends by which ones fit within the currently selected period
    return jobAssignmentDates.filter(dates => 
      // Check if the start is within the period
      moment(dates.start).isBetween(moment(this.startPeriod), moment(this.endPeriod), "day", "[]")
      // Or the end is within the period
      || moment(dates.end).isBetween(moment(this.startPeriod), moment(this.endPeriod), "day", "[]")
      // Or the period start is within the dates
      || moment(this.startPeriod).isBetween(moment(dates.start), moment(dates.end), "day", "[]")
      // Or the period end is within the dates
      || moment(this.endPeriod).isBetween(moment(dates.start), moment(dates.end), "day", "[]")
      // And finally check the boundaries
      || moment(dates.start).isSame(moment(this.startPeriod))
      || moment(dates.start).isSame(moment(this.endPeriod))
      || moment(dates.end).isSame(moment(this.startPeriod))
      || moment(dates.end).isSame(moment(this.endPeriod))
    );
  }

  get filteredJobs(): Job[] {
    return vxm.jobs.jobs
      .filter(job => this.getJobAssignmentDates(job).length > 0)
      .sort((a, b) => {
        let aStart = this.getJobAssignmentDates(a).map(x => x.start).find(() => true);
        let bStart = this.getJobAssignmentDates(b).map(x => x.start).find(() => true);

        if(moment(aStart) < moment(bStart)){
          return -1;
        }

        if(moment(aStart) > moment(bStart)){
          return 1;
        }

        if(a.startTime && b.startTime) {
          if (a.startTime < b.startTime) {
            return -1;
          }

          if (a.startTime > b.startTime) {
            return 1;
          }
        }

        return 0;
      });
  }

  get jobsLoadError(): string {
    return vxm.jobs.jobsLoadError;
  }

  get loadingJobs(): boolean {
    return vxm.jobs.loadingJobs;
  }

  get dateType(): JobsViewerModeEnum {
    return vxm.jobs.jobsViewerMode;
  }

  set dateType(value: JobsViewerModeEnum) {
    vxm.jobs.setJobsViewerMode(value);
  }

  get selectedDayReadable() {
    return moment(this.selectedDay).format(DATE_FORMAT_READABLE);
  }

  get selectedDayEndOfWeekReadable() {
    // Today counts as 1 + 6 = 7 (i.e., a week)
    return moment(this.selectedDay).add(6, "days").format(DATE_FORMAT_READABLE);
  }

  get selectedMonthReadable() {
    return moment(this.selectedMonth + "-01").format(MONTH_FORMAT_READABLE);
  }

  get selectedDay(): string {
    return vxm.jobs.jobsViewerSelectedDay;
  }

  set selectedDay(value: string) {
    vxm.jobs.setSelectedDay(value);
  }

  get selectedMonth(): string {
    return vxm.jobs.jobsViewerSelectedMonth;
  }

  set selectedMonth(value: string) {
    vxm.jobs.setSelectedMonth(value);
  }

  get dateTypes() {
    return Object.values(JobsViewerModeEnum);
  }
}

